import { useState, useEffect } from "react";
import {
  Box,
  Flex,
  Icon,
  Image,
  Spacer,
  Tag,
  TagLabel,
  Text,
  Wrap,
  useColorMode,
  useColorModeValue,
} from "@chakra-ui/react";
import {
  Card,
  Stack,
  Title,
  Grid,
  Button,
  Modal,
  useMantineTheme,
  Group,
} from "@mantine/core";
import Select from "react-select";
import { FcParallelTasks } from "react-icons/fc";

// API requests
import { getClients } from "api/requests/getClients";
import {
  getZohoClients,
  getTaskList,
  addTaskToProject,
} from "api/requests/zohoRequests";
import { FaAngleDown, FaAngleUp } from "react-icons/fa";

function TaskManager() {
  const { colorMode } = useColorMode();
  const [client, setClient] = useState();
  const [clients, setClients] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [isCollapsed, setIsCollapsed] = useState({
    globalRecurring: false,
    newClientStart: false,
    monthlyRecurring: false,
    monthlyCreativeRecurring: false,
  });

  const toggleCollapse = (section) => {
    setIsCollapsed((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const [zohoClients, setZohoClients] = useState([]);
  const [zohoTaskLists, setZohoTaskLists] = useState([]);
  const [newClientStart, setNewClientStart] = useState([]);
  const [recurring, setSetRecurring] = useState([]);
  const [recurringCreative, setRecurringCreative] = useState([]);
  const [clientRecurring, setClientRecurring] = useState();
  const [globalRecurring, setGlobalRecurring] = useState([]);

  const [taskIds, setTaskIds] = useState({
    coop: {
      id: "1290214000007226308",
    },
    creative: {
      id: "1290214000007226478",
    },
    ga4: {
      id: "1290214000007226518",
    },
    googleDisplay: {
      id: "1290214000007226630",
    },
    googleVla: {
      id: "1290214000007226646",
    },
    ott: {
      id: "1290214000007226578",
    },
    paidSearch: {
      id: "1290214000007226766",
    },
    paidSocial: {
      id: "1290214000007226678",
    },
    serviceAdPackage: {
      id: "1290214000007226422",
    },
    spanishAdPackage: {
      id: "",
    },
    techSeo: {
      id: "1290214000007761131",
    },
    websiteManagement: {
      id: "1290214000007226879",
    },
    seoEnhanced: {
      id: "1290214000007522127",
    },
    seoUltimate: {
      id: "1290214000007522137",
    },
  });

  const labels = clients;
  const [newTitles, setNewTitles] = useState("empty");
  const newOption = newTitles === "empty" ? labels : newTitles;
  const [value, setValue] = useState();

  const defaultAccount = {
    label: "Overview",
    value: "overview",
  };

  // colors
  const bgColor = useColorModeValue("white", "#4A5058");
  const bgColor2 = "rgba(255, 255, 255, 0.11)";
  const textColor = useColorModeValue("gray.700", "white");
  const bgCard = useColorModeValue(
    "gray.200",
    "linear-gradient(81.62deg, #343A40 2.25%, #212529 79.87%)"
  );
  const bgCard2 = useColorModeValue(
    "linear-gradient(81.62deg, #3182CE 2.25%, #2B6CB0 79.87%)",
    "linear-gradient(81.62deg, #343A40 2.25%, #212529 79.87%)"
  );

  // Modal
  const [noTransitionOpened, setNoTransitionOpened] = useState(false);
  const [slowTransitionOpened, setSlowTransitionOpened] = useState(false);
  const theme = useMantineTheme();
  const [modalValue, setModalValue] = useState();
  const modalTaskLists = zohoTaskLists.map((tasklist) => ({
    id: tasklist.id,
    label: tasklist.name,
    value: tasklist.name.toString().replace(/\W/g, ""),
  }));

  const fetchZohoClients = async () => {
    try {
      const data = await getZohoClients();
      setZohoClients(data);
      console.log("Clients:", data);
    } catch (error) {
      console.error("Failed to fetch clients:", error);
    }
  };

  const fetchTaskLists = async () => {
    try {
      const data = await getTaskList();
      setZohoTaskLists(data?.task_list_templates);
      const newClientStartData = data?.task_list_templates.filter(
        (template) =>
          template.name.includes("[New Client Start-up]") ||
          template.name.includes("[ONBOARDING GENERAL]")
      );
      const recurringData = data?.task_list_templates.filter(
        (template) =>
          template.name.includes("[RECURRING]") &&
          !template.name.includes("[RECURRING] - Creative Team Task List")
      );
      const recurringCreativeData = data?.task_list_templates.filter(
        (template) =>
          template.name.includes("[RECURRING] - Creative Team Task List")
      );
      setNewClientStart(newClientStartData);
      setSetRecurring(recurringData);
      setRecurringCreative(recurringCreativeData);
    } catch (error) {
      console.error("Failed to fetch Task Lists:", error);
    }
  };

  const colorStyles = {
    control: (styles) => ({
      ...styles,
      backgroundColor: useColorModeValue("white", "#495057"),

      padding: "4px",
      borderRadius: "4px",
      border: "none",
    }),
    option: (styles, { isFocused, isSelected }) => ({
      ...styles,
      backgroundColor:
        isSelected && colorMode === "dark"
          ? "#3182ce"
          : isFocused
          ? "#3182ce"
          : undefined,
      color: useColorModeValue("gray.200", "white"),
    }),
    singleValue: (styles, { isFocused }) => {
      return {
        ...styles,
        color: useColorModeValue("white", "white"),
        backgroundColor:
          colorMode === "dark" ? "#3182ce" : isFocused ? "#d0dcfb" : "#3182ce",
        padding: "5px",
        borderRadius: "4px",
        fontSize: "85%",
        width: "fit-content",
      };
    },
    menu: (styles) => ({
      ...styles,
      backgroundColor: useColorModeValue("white", "#495057"),
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  };

  const handleOnClick = (value) => {
    if (value && value?.label) {
      setClient(value);
    } else {
      setValue();
      setClient();
    }
  };

  useEffect(() => {
    const initialRecurringTasks = Object.entries(client?.products || {}).reduce(
      (acc, [productName, productDetails]) => {
        if (productDetails.status) {
          // Check if the product's status is true
          const taskId = taskIds[productName]?.id;
          if (taskId) {
            // Ensure there is a corresponding task ID
            const matchingTask = zohoTaskLists.find(
              (task) => task.id === taskId
            );
            if (matchingTask) {
              acc.push({
                id: matchingTask.id,
                name: matchingTask.name,
                status: true,
              });
            }
          }
        }
        return acc;
      },
      []
    );
    setClientRecurring(initialRecurringTasks);
  }, [client, zohoTaskLists]);

  const fetchClients = async () => {
    try {
      const data = await getClients();
      const filteredData = data.filter(
        (client) => client.status !== false && client.brand !== "AutoGo"
      );
      setClients(filteredData);
    } catch (error) {
      console.error("Failed to fetch clients:", error);
    }
  };

  const handleClientUpdate = async () => {
    await fetchClients();
  };

  useEffect(() => {
    fetchClients();
    fetchTaskLists();
  }, []);

  const handleTasks = () => {
    const tasksToProcess = client ? clientRecurring : recurring;

    const tasks = tasksToProcess.map((taskList) => (
      <Tag
        size={"md"}
        key={taskList.id}
        borderRadius={"md"}
        variant={"solid"}
        colorScheme={"blue"}
      >
        <TagLabel>{taskList.name}</TagLabel>
      </Tag>
    ));

    return tasks;
  };

  const handleAddTasks = async (i) => {
    const data = {
      id: client?.projectId,
      taskLists: {
        id: i.id,
      },
    };
    console.log(data);
    try {
      const response = await addTaskToProject(data);
      console.log(response);
    } catch (error) {
      console.error("Failed to add Tasks to Project:", error);
    }
  };

  return (
    <Box direction="column" pt={{ sm: "120px", md: "75px" }}>
      {/* Task Manager Header */}
      <Card bg={bgColor} mb={"md"} withBorder>
        <Grid grow>
          <Grid.Col span={4} my={"md"}>
            <Stack direction="column">
              <Card bg={bgCard2}>
                <Flex justify="space-between" w="100%" align="center">
                  <Flex direction="column">
                    <Text fontSize="md" color={"white"} fontWeight="bold">
                      {client?.label ? client?.label : defaultAccount?.label}
                    </Text>
                  </Flex>
                </Flex>
              </Card>
              <Card bg={bgCard2} p={"xs"}>
                <Select
                  menuPortalTarget={document.body}
                  isClearable
                  isDisabled={isLoading}
                  isLoading={isLoading}
                  onChange={(newValue) => handleOnClick(newValue)}
                  options={newOption}
                  value={value}
                  styles={colorStyles}
                  placeholder="Search Clients..."
                />
              </Card>
            </Stack>
          </Grid.Col>
          <Grid.Col span={4}>
            <Card p="lg" bg={bgColor}>
              <Stack
                spacing="lg"
                align="center"
                sx={{ height: "100%", position: "relative" }}
              >
                <Title color={textColor} order={1}>
                  Task Manager
                </Title>
                <Text color={textColor}>
                  Here you can manage all things task related.
                </Text>
                <Text color={textColor}>
                  {client ? `Project ID: ${client?.projectId}` : null}
                </Text>
              </Stack>
            </Card>
          </Grid.Col>
          <Grid.Col span={4}>
            <Card bg={bgCard2}>
              <Flex
                direction="column"
                color={"white"}
                h="100%"
                p="0px 10px 20px 10px"
                w="100%"
              >
                <Flex justify="space-between" align="center">
                  <Text fontWeight="bold">
                    <u>TaskList Info</u>
                  </Text>
                  <Icon
                    as={FcParallelTasks}
                    w="48px"
                    h="auto"
                    color="gray.400"
                  />
                </Flex>
                <Spacer />
                <Flex direction="column" gap={4}>
                  <Box>
                    <Text fontSize="l" letterSpacing="2px" fontWeight="bold">
                      Current Onboarding TaskList: {newClientStart?.length}
                    </Text>
                  </Box>
                  <Box>
                    <Text fontSize="l" letterSpacing="2px" fontWeight="bold">
                      Current Recurring TaskList: {recurring?.length}
                    </Text>
                  </Box>
                </Flex>
              </Flex>
            </Card>
          </Grid.Col>
        </Grid>
      </Card>
      <Flex direction={"column"} gap={3.5}>
        {/* Global Recurring Tasks */}
        <Card bg={bgColor} withBorder>
          <Box
            style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
            onClick={() => toggleCollapse("globalRecurring")}
          >
            <Title order={5} color={textColor} pr={10}>
              Global Recurring Tasks
            </Title>
            {isCollapsed.globalRecurring ? (
              <FaAngleDown style={{ color: "gray" }} />
            ) : (
              <FaAngleUp style={{ color: "gray" }} />
            )}
          </Box>
          {isCollapsed.globalRecurring && (
            <Box
              border={"1px"}
              borderColor={"gray.200"}
              borderRadius={"4px"}
              my={5}
              p={2}
            >
              <Wrap spacing={2}>
                {globalRecurring.map((taskList) => (
                  <Tag
                    size={"md"}
                    key={taskList.id}
                    borderRadius={"md"}
                    variant={"solid"}
                    colorScheme={"blue"}
                  >
                    <TagLabel>{taskList.name}</TagLabel>
                  </Tag>
                ))}
              </Wrap>
            </Box>
          )}
        </Card>
        {/* New Client Start-up Tasks */}
        {client ? null : (
          <Card bg={bgColor} withBorder>
            <Box
              style={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
              }}
              onClick={() => toggleCollapse("newClientStart")}
            >
              <Title order={5} color={textColor} pr={10}>
                New Client Start-up Tasks
              </Title>
              {isCollapsed.newClientStart ? (
                <FaAngleDown style={{ color: "gray" }} />
              ) : (
                <FaAngleUp style={{ color: "gray" }} />
              )}
            </Box>
            {isCollapsed.newClientStart && (
              <Box
                border={"1px"}
                borderColor={"gray.200"}
                borderRadius={"4px"}
                my={5}
                p={2}
              >
                <Wrap spacing={2}>
                  {newClientStart.map((taskList) => (
                    <Tag
                      size={"md"}
                      key={taskList.id}
                      borderRadius={"md"}
                      variant={"solid"}
                      colorScheme={"blue"}
                    >
                      <TagLabel>{taskList.name}</TagLabel>
                    </Tag>
                  ))}
                </Wrap>
              </Box>
            )}
          </Card>
        )}
        {/* Monthly Recurring Tasks */}
        <Card bg={bgColor} withBorder>
          <Box
            style={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
            }}
            onClick={() => toggleCollapse("monthlyRecurring")}
          >
            <Title order={5} color={textColor} pr={10}>
              Monthly Recurring Tasks
            </Title>
            {isCollapsed.monthlyRecurring ? (
              <FaAngleDown style={{ color: "gray" }} />
            ) : (
              <FaAngleUp style={{ color: "gray" }} />
            )}
          </Box>
          {isCollapsed.monthlyRecurring && (
            <>
              <Box
                border={"1px"}
                borderColor={"gray.200"}
                borderRadius={"4px"}
                mt={5}
                mb={3}
                p={2}
              >
                <Wrap spacing={2}>{handleTasks()}</Wrap>
              </Box>
              <Flex justify={"flex-end"} align={"center"}>
                {client ? (
                  <Button onClick={() => setSlowTransitionOpened(true)}>
                    Load TaskList
                  </Button>
                ) : null}
              </Flex>
            </>
          )}
        </Card>
        {/* Creative Team Recurring Tasks */}
        {client ? null : (
          <Card bg={bgColor} withBorder>
            <Box
              style={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
              }}
              onClick={() => toggleCollapse("monthlyCreativeRecurring")}
            >
              <Title order={5} color={textColor} pr={10}>
                Creative Team Recurring Tasks
              </Title>
              {isCollapsed.monthlyCreativeRecurring ? (
                <FaAngleDown style={{ color: "gray" }} />
              ) : (
                <FaAngleUp style={{ color: "gray" }} />
              )}
            </Box>
            {isCollapsed.monthlyCreativeRecurring && (
              <Box
                border={"1px"}
                borderColor={"gray.200"}
                borderRadius={"4px"}
                my={5}
                p={2}
              >
                <Wrap spacing={2}>
                  {recurringCreative.map((taskList) => (
                    <Tag
                      size={"md"}
                      key={taskList.id}
                      borderRadius={"md"}
                      variant={"solid"}
                      colorScheme={"blue"}
                    >
                      <TagLabel>{taskList.name}</TagLabel>
                    </Tag>
                  ))}
                </Wrap>
              </Box>
            )}
          </Card>
        )}
      </Flex>
      <Modal
        centered
        opened={slowTransitionOpened}
        onClose={() => setSlowTransitionOpened(false)}
        size={"md"}
        withCloseButton={false}
        closeOnClickOutside={false}
        title={
          <Text fontWeight={"bold"} fontSize={"large"}>
            Are you sure?
          </Text>
        }
        overlayProps={{
          color:
            theme.colorScheme === "dark"
              ? theme.colors.dark[9]
              : theme.colors.gray[2],
          opacity: 0.55,
          blur: 3,
        }}
        styles={{
          header: {
            color: "red",
            backgroundColor: "#333",
          },
          content: {
            color: "white",
            backgroundColor: "#333",
          },
        }}
      >
        <Text pb={5}>
          This will load the selected task to the clients project within Zoho
          Projects. Please make sure the task isn't already loaded.
        </Text>
        <Select
          menuPortalTarget={document.body}
          isClearable
          isDisabled={isLoading}
          isLoading={isLoading}
          onChange={(newValue) =>
            newValue ? setModalValue(newValue) : setModalValue()
          }
          options={modalTaskLists}
          value={modalValue}
          styles={colorStyles}
          placeholder="Select a TaskLists..."
        />
        <Group mt={"lg"} position="right">
          <Button
            variant="outline"
            color="green"
            onClick={() => handleAddTasks(modalValue)}
          >
            Load Task
          </Button>
          <Button
            variant="outline"
            color="red"
            onClick={() => {
              setModalValue();
              setSlowTransitionOpened(false);
            }}
          >
            Cancel
          </Button>
        </Group>
      </Modal>
      {/* <Button mt={10} onClick={() => console.log(modalValue)}>
        Test
      </Button> */}
    </Box>
  );
}

export default TaskManager;
